// sim/ecosystemExample.ts — headless ecosystem demo + the world builder.
//
// `buildEcosystemWorld(config)` MIRRORS buildColonyWorld's wiring (SoA store, grid,
// genomes, colonies, pathing, genes, tasks, AI, organism loop, resources) and then
// layers the ADDITIVE ecosystem: a WildlifeSystem driven by an INDEPENDENT RNG
// stream (so wildlife randomness never perturbs colony determinism) plus an
// EcosystemSystem (population dynamics). The wildlife layer talks to the colony
// only through three small seams (presence / prey-kill / food-raid), so it runs
// with OR without colonies.
//
// The determinism test and the CLI both import this, so the demo and the test
// exercise ONE code path.
//
// Run it directly:  node src/sim/ecosystemExample.ts --colonies 2 --ticks 200 --map

import {
  KIND,
  CASTE,
  NIL,
  SPECIES,
  SPECIES_COUNT,
  ECO_EVENT,
  ECO_EVENT_COUNT,
  APEX_KIND_COUNT,
  APEX_OUTCOME,
} from '../types.ts';
import type {
  EcosystemWorldConfig,
  EcosystemEventConfig,
  SpeciesConfig,
  EcosystemConfig,
  ApexWorldConfig,
  EcosystemPressureConfig,
  LandmarkConfig,
  EcoEvent,
  EntityId,
  Species,
  Caste,
  ColonyId,
  System,
  Tick,
} from '../types.ts';
import { Rng } from '../rng.ts';
import { fnvInit, fnvU32, hashHex } from '../hash.ts';
import { EntitySystem } from '../engine/EntitySystem.ts';
import { WorldGrid } from '../engine/WorldGrid.ts';
import { GameLoop } from '../engine/GameLoop.ts';
import { GreedyPathing } from '../engine/Pathing.ts';
import { DangerField, DangerAwarePathing } from './dangerField.ts';
import type { DangerFieldConfig } from './dangerField.ts';
import { ResourceSystem } from './resources.ts';
import { ColonySystem } from './colony.ts';
import { OrganismSystem } from './organisms.ts';
import { TaskQueue } from './taskQueue.ts';
import type { AutonomyCtx } from './taskQueue.ts';
import { ColonyAISystem } from './colonyAI.ts';
import { GenomeSystem, baseProfile } from './genome.ts';
import { GeneInventory } from './genes.ts';
import { LeaderboardSystem } from './leaderboard.ts';
import { WildlifeSystem } from './wildlife.ts';
import type {
  WildlifeCtx,
  ColonyPresence,
  PreyKillSink,
  RaidableFood,
} from './wildlife.ts';
import { EcosystemSystem } from './ecosystem.ts';
import { EcosystemEventSystem, neutralModifiers, MAX_PREDATOR_BURST } from './ecosystemEvents.ts';
import type { EcosystemModifiers } from './ecosystemEvents.ts';
import { ApexSystem } from './apex.ts';
import type { ApexBehaviorConfig } from './apex.ts';
import { EcosystemPressureSystem } from './ecosystemPressure.ts';
import { LandmarkSystem, landmarkName, landmarkGlyph, landmarkTargetCount } from './landmarks.ts';
import { HiveSystem } from './hive.ts';
import { DiscoverySystem } from './discovery.ts';
import {
  defaultCasteConfigs,
  defaultColonyConfig,
  defaultGeneConfig,
  defaultColonyAIConfig,
  defaultLeaderboardConfig,
  defaultPersonalityConfig,
  defaultHiveConfig,
  computeColonyStateHash,
  renderColony,
} from './colonyExample.ts';
import type { ColonyWorld } from './colonyExample.ts';
import { ColonyPersonality } from './personality.ts';
import { ColonyProjectSystem, defaultProjectsConfig } from './projects.ts';
import type { ProjectsConfig } from './projects.ts';
import { CarryingCapacitySystem, defaultCapacityConfig } from './capacity.ts';
import { computeColonyDiagnostics, diagnosticsCensusLine } from './diagnostics.ts';
import type { DiagnosticsCtx } from './diagnostics.ts';
import { LAYER, layerPeriod, hashTimescale } from './timescale.ts';
import type { TimescaleConfig } from './timescale.ts';

// ---------------------------------------------------------------------------
// QUEEN-ENTITY threat curve (opt-in, only when config.colony.queenEntity). Maps the
// ecosystem-pressure system's proximity-weighted predation+aerial sum into per-tick
// queen damage. UNTUNED — module constants, CALIBRATED against the measured pressure
// scale near a player nest in the playable density (over a long run: 0 ~83% of ticks,
// 1-4 occasionally, 5-9 rarely, ~9 the sustained ceiling; each nearby predator adds
// w = pressureRadius − dist + 1, i.e. 1..8 with radius 7, and a boss adds w×4). Chosen
// so the HP dip is a REAL-SIEGE signal, not constant chip:
//   • LIGHT pressure (≤ FLOOR — the common 1-4 casual case) deals ZERO damage, so her
//     HP holds at full when the nest is merely passed by;
//   • the floor is subtracted, then a >> SHIFT scales it, then a CAP bounds it, so a
//     SUSTAINED CLUSTER (a Spider/Bird territory parked on the nest, an apex camp, a
//     swarm) crosses QUEEN_REGEN (2) and her HP visibly DIPS — then REGENS back once
//     the threat lifts. A genuine swarm is capped at CAP/tick so she is battered but
//     always recovers when calm (she floors at 1 in damageQueen — never dies).
// Tune these against real apex/predation pressure if the curve feels off.
const QUEEN_THREAT_FLOOR = 4; // pressure at/below this ⇒ 0 damage (casual traffic is free)
const QUEEN_THREAT_SHIFT = 1; // (pressure − FLOOR) >> SHIFT ⇒ integer damage ramp
const QUEEN_THREAT_CAP = 6;   // hard cap per tick (a battered queen still recovers when calm)

// ---------------------------------------------------------------------------
// World container — a structural SUPERSET of ColonyWorld (so the shared
// computeColonyStateHash accepts it) plus the ecosystem layer.
// ---------------------------------------------------------------------------

export interface EcosystemWorld extends ColonyWorld {
  /** The wildlife AI system (non-colony life). */
  readonly wildlife: WildlifeSystem;
  /** The ecosystem population-dynamics system. */
  readonly ecosystem: EcosystemSystem;
  /** The INDEPENDENT RNG stream feeding wildlife/ecosystem. */
  readonly wildlifeRng: Rng;
  /**
   * Dynamic-ecosystem-event scheduler — present ONLY when config.events is set.
   * Absent ⇒ the world is byte-identical to a pre-events ecosystem world.
   */
  readonly events?: EcosystemEventSystem | undefined;
  /** The live event-modifier struct (present iff `events` is). */
  readonly eventMods?: EcosystemModifiers | undefined;
  /**
   * Apex (boss) system — present ONLY when config.apex is set. Absent ⇒ the world
   * is byte-identical to a pre-apex ecosystem world (hash fold is guarded).
   */
  readonly apex?: ApexSystem | undefined;
  /**
   * Wildlife→colony pressure — present ONLY when config.pressure is set. Absent ⇒
   * the world is byte-identical (the AI's pressure hook stays unwired; hash guarded).
   */
  readonly pressure?: EcosystemPressureSystem | undefined;
  /** Region-scale colony projects — the long-term build objective layer. */
  readonly projects?: ColonyProjectSystem | undefined;
  /** Carrying capacity — the population-stability foundation (capacity + pressure). */
  readonly capacity?: CarryingCapacitySystem | undefined;
  /** Named map landmarks — present ONLY when config.landmarks is set. Absent ⇒ byte-identical. */
  readonly landmarks?: LandmarkSystem | undefined;
  /**
   * The layered-timescale ladder — present ONLY when config.timescale is set. When
   * set it drives the capacity/AI/leaderboard/projects cadences off the four-layer
   * ladder; absent ⇒ those keep their pre-timescale defaults and the world (and its
   * hash) is byte-identical to a pre-timescale build (the hash fold is guarded).
   */
  readonly timescale?: TimescaleConfig | undefined;
  /**
   * Predator-DANGER cost field — present ONLY when config.dangerAvoidance is set.
   * When wired it rebuilds each tick from live predator positions and a danger-aware
   * bounded A* routes the colony FORAGERS around predator territory (the literal
   * "route change" the BACKLOG asked for). It is a DERIVED CACHE of already-hashed
   * wildlife state, so it is deliberately NOT folded into computeEcosystemStateHash.
   * Absent ⇒ foragers stay on greedy and the world is byte-identical.
   */
  readonly dangerField?: DangerField | undefined;
  /**
   * Evolution-DISCOVERY observer — present ONLY when config.discovery is set. It watches
   * each colony's gene pool and auto-discovers emergent "morph" species. Like the danger
   * field it is a PURE DERIVED view of already-hashed gene state (no RNG, observe-only), so
   * it is deliberately NOT folded into computeEcosystemStateHash — on load it recomputes
   * identically from the replayed genes. Absent ⇒ the world is byte-identical.
   */
  readonly discovery?: DiscoverySystem | undefined;
}

// ---------------------------------------------------------------------------
// Default tuning (shared by the CLI and tests). EXACT values per the spec.
// ---------------------------------------------------------------------------

/** Per-species defaults, indexed by SPECIES.* (Spider, Bird, Rat, Frog, Beetle, Dragonfly). */
export function defaultSpeciesConfigs(): SpeciesConfig[] {
  const cfgs: SpeciesConfig[] = [];
  // WORLD-SCALE: territoryRadius / roamRadius are bumped MODERATELY so predator
  // territories become meaningful ZONES a colony must route around on the
  // ~7.4x-larger default map — but they stay LOCAL, not map-spanning. senseRadius
  // is held modest (unchanged) so a predator still has to be near to strike; the
  // bug isn't omniscient just because the world grew. The flying/migratory species
  // (Bird, Dragonfly) are allowed to range a little farther than ground ambushers.
  cfgs[SPECIES.Spider] = {
    // Ambush territory 4 -> 5: a slightly wider lair to skirt, still a fixed local zone.
    startEnergy: 60, energyDecay: 1, startHp: 18, senseRadius: 3, attackPower: 5,
    stepsPerTick: 1, roamRadius: 1, territoryRadius: 5, feedEnergy: 28, migrateInterval: 0,
    flying: false, migrates: false, eatsInsects: true, eatsFood: false,
  };
  cfgs[SPECIES.Bird] = {
    // Migratory aerial hunter — range is governed by full-map migration, not a
    // territory/roam radius, so it already spans the bigger world. Left as-is.
    startEnergy: 70, energyDecay: 1, startHp: 10, senseRadius: 6, attackPower: 4,
    stepsPerTick: 2, roamRadius: 0, territoryRadius: 0, feedEnergy: 24, migrateInterval: 40,
    flying: true, migrates: true, eatsInsects: true, eatsFood: false,
  };
  cfgs[SPECIES.Rat] = {
    // Omnivore raider — no fixed territory; roam 6 -> 7 so its larder-raiding
    // range covers a bit more of the larger map between colony anchors.
    startEnergy: 65, energyDecay: 1, startHp: 14, senseRadius: 5, attackPower: 3,
    stepsPerTick: 1, roamRadius: 7, territoryRadius: 0, feedEnergy: 20, migrateInterval: 0,
    flying: false, migrates: true, eatsInsects: true, eatsFood: true,
  };
  cfgs[SPECIES.Frog] = {
    // Ambush territory 3 -> 4: a slightly wider pond zone, still fixed and local.
    startEnergy: 50, energyDecay: 1, startHp: 12, senseRadius: 3, attackPower: 5,
    stepsPerTick: 1, roamRadius: 1, territoryRadius: 4, feedEnergy: 24, migrateInterval: 0,
    flying: false, migrates: false, eatsInsects: true, eatsFood: false,
  };
  // Beetle — armoured ground SCAVENGER: high hp, slow (1 step), never hunts
  // (attackPower 0, eatsInsects false), raids FOOD nodes, drifts near home.
  cfgs[SPECIES.Beetle] = {
    // Roam 3 -> 4: drifts a bit wider while scavenging food across the big map.
    startEnergy: 70, energyDecay: 1, startHp: 30, senseRadius: 4, attackPower: 0,
    stepsPerTick: 1, roamRadius: 4, territoryRadius: 0, feedEnergy: 22, migrateInterval: 0,
    flying: false, migrates: false, eatsInsects: false, eatsFood: true,
  };
  // Dragonfly — fast FLYING TERRITORIAL hunter: low hp, high speed (3 steps),
  // hunts insects ONLY within territoryRadius of home, patrols home otherwise.
  cfgs[SPECIES.Dragonfly] = {
    // Flying territorial — territory 5 -> 6, roam 2 -> 3: a slightly wider patrolled
    // hunting zone (a flyer ranges a bit more than a ground ambusher) but still local.
    startEnergy: 55, energyDecay: 1, startHp: 8, senseRadius: 5, attackPower: 4,
    stepsPerTick: 3, roamRadius: 3, territoryRadius: 6, feedEnergy: 22, migrateInterval: 0,
    flying: true, migrates: false, eatsInsects: true, eatsFood: false,
  };
  return cfgs;
}

/** Ecosystem population-dynamics defaults (shared by the CLI and tests). */
export function defaultEcosystemConfig(): EcosystemConfig {
  return {
    spawnInterval: 12,
    baseCapacity: 6,
    capacityPerAnchor: 2,
    maxWildlife: 32,
    spawnsPerPass: 2,
    // Order: Spider, Bird, Rat, Frog, Beetle, Dragonfly.
    initialCounts: [2, 2, 2, 1, 1, 1],
  };
}

/** Dynamic-ecosystem-event defaults (shared by the CLI and tests). */
export function defaultEcosystemEventConfig(): EcosystemEventConfig {
  return {
    minGap: 40,
    gapJitter: 40,
    minDuration: 15,
    durationJitter: 15,
    weights: [1, 1, 1, 1, 1],
  };
}

/**
 * Apex (boss) defaults (shared by the CLI and tests). RARE spawns (checked every
 * 30 ticks, 8% per check) and MASSIVE hp pools so a boss is a multi-colony siege,
 * not a casual kill. Order: Huntsman, CrowMatriarch, RatKing, PondLeviathan.
 */
export function defaultApexConfig(): ApexWorldConfig {
  return {
    spawnCheckInterval: 30,
    spawnChancePct: 8,
    soldierDamage: 2,
    historyCap: 16,
    alertCap: 16,
    // --- Territory + mode state machine (tuned for the large playable world,
    //     150x88; the boss is an ECOSYSTEM FORCE, not a nest assassin). ---
    // A meaningful patrol zone on a large map — wide enough to roam, bounded so the
    // boss does not chase organisms across the whole world.
    territoryRadius: 14,
    // VOLUNTARY migration on a slow, world-shaping cadence (Evolution-timescale
    // spirit, docs/TIMESCALE.md: 192-768 band; lower end keeps the world dynamic).
    migrateInterval: 240,
    // Post-kill WITHDRAWAL ~tens of ticks: long enough to break off a nest, short
    // enough that the boss re-engages real threats soon.
    retreatTicks: 30,
    // "Near a nest" ≈ the widest boss senseRadius (9) + a margin, so a boss is felt
    // as camping well before it sits on a spawn point.
    nestBuffer: 12,
    // Modest: camping breaks within ~a minute of sim time (≈360 ticks @ ref tps 6),
    // and proximity only accrues while actually beside a nest.
    maxNestProximityTicks: 40,
    // After a forced migration, avoid hunting toward / stepping onto nests for a
    // good window so the boss genuinely relocates.
    nestAvoidTicks: 120,
    // A fledgling ~10-member colony is BENEATH the apex's notice; an established
    // colony (≥18) is a significant actor worth hunting.
    significancePop: 18,
    // Candidate tiles scored when picking a territory (bounded, deterministic RNG).
    territorySamples: 24,
    // Box-scan radius scoring a candidate's food/wildlife/contested richness.
    scoreRadius: 6,
    // Rich food zones attract; prey concentrations attract more; a contested
    // frontier (multiple colonies) is the prize; nest proximity is penalised.
    foodWeight: 2,
    wildlifeWeight: 3,
    contestedWeight: 6,
    nestPenalty: 5,
    configs: [
      // Huntsman — fast ground hunter, modest sight.
      {
        maxHp: 220, attackPower: 8, senseRadius: 6, stepsPerTick: 2, flying: false,
        vulnRadius: 1, geneQualityBonus: 40, summonInterval: 0, maxLifespan: 600,
      },
      // CrowMatriarch — flying, widest sight + reach (ignores walls).
      {
        maxHp: 180, attackPower: 7, senseRadius: 9, stepsPerTick: 3, flying: true,
        vulnRadius: 1, geneQualityBonus: 45, summonInterval: 0, maxLifespan: 600,
      },
      // RatKing — ground hunter that summons rat reinforcements every 8 ticks.
      {
        maxHp: 260, attackPower: 6, senseRadius: 6, stepsPerTick: 1, flying: false,
        vulnRadius: 2, geneQualityBonus: 50, summonInterval: 8, maxLifespan: 700,
      },
      // PondLeviathan — slow, enormously tanky, area-denial (devours wildlife).
      {
        maxHp: 360, attackPower: 10, senseRadius: 4, stepsPerTick: 1, flying: false,
        vulnRadius: 2, geneQualityBonus: 60, summonInterval: 0, maxLifespan: 800,
      },
    ],
  };
}

/**
 * Ecosystem-pressure defaults. pressureRadius spans the colony's foraging reach so
 * a nest's neighbourhood is "felt"; a calm scanInterval keeps adaptation gradual.
 * Squelch thresholds are proximity-weighted sums (a couple of close predators trip
 * them); biasDivisor/Cap keep the need nudge small so survival still dominates.
 */
export function defaultEcosystemPressureConfig(): EcosystemPressureConfig {
  return {
    scanInterval: 12,
    pressureRadius: 7,
    biasDivisor: 4,
    biasCap: 4,
    theftDrain: 2,
    forageSquelch: 6,
    aerialSquelch: 6,
  };
}

/**
 * Named-landmark defaults (shared by the CLI and tests). A handful of major
 * features on a default map, each shaping a radius-3 neighbourhood (food larder /
 * wildlife territory / impassable barrier), with their wildlife territories topped
 * up every 90 ticks so a Bird Nest or Spider's rocks stay inhabited.
 *
 * DENSITY-SCALED: `densityPer` ~one landmark per 900 tiles, floored at `count` (6)
 * and capped at `maxCount` (24). A small/CLI map (≤ ~5400 tiles) still gets the
 * baseline 6 — roughly the old feel — while the big playable world (150×88 ≈ 13.2k
 * tiles) gets ~15, so the map reads as scaled, varied places instead of a fixed set.
 */
export function defaultLandmarkConfig(): LandmarkConfig {
  return { count: 6, radius: 3, respawnInterval: 90, densityPer: 900, maxCount: 24 };
}

// ---------------------------------------------------------------------------
// Colony-presence seam: anchors = nests (homeXAt/homeYAt) THEN hive nodes, in
// deterministic order. This is the colony-expansion signal wildlife reacts to.
// Reading it lazily (no cached array) keeps it allocation-free and always live.
// ---------------------------------------------------------------------------

function buildPresence(colonies: ColonySystem, ai: ColonyAISystem): ColonyPresence {
  return {
    anchorCount(): number {
      let total = colonies.count;
      for (let cid = 0; cid < colonies.count; cid++) total += ai.hiveCountAt(cid);
      return total;
    },
    anchorX(i: number): number {
      const n = colonies.count;
      if (i < n) return colonies.homeXAt(i);
      // Index into the hive nodes, colony-major, in deterministic order.
      let rem = i - n;
      for (let cid = 0; cid < n; cid++) {
        const h = ai.hiveCountAt(cid);
        if (rem < h) return ai.hiveXAt(cid, rem);
        rem -= h;
      }
      return 0;
    },
    anchorY(i: number): number {
      const n = colonies.count;
      if (i < n) return colonies.homeYAt(i);
      let rem = i - n;
      for (let cid = 0; cid < n; cid++) {
        const h = ai.hiveCountAt(cid);
        if (rem < h) return ai.hiveYAt(cid, rem);
        rem -= h;
      }
      return 0;
    },
  };
}

// ---------------------------------------------------------------------------
// World builder.
// ---------------------------------------------------------------------------

/**
 * `EcosystemWorldConfig` widened with an OPTIONAL region-projects override (kept out
 * of the shared `types.ts` to avoid a projects→types import cycle, mirroring how the
 * `timescale` field stays dependency-light). When `projects` is absent the projects
 * system is built from `defaultProjectsConfig()` exactly as before ⇒ byte-identical.
 * Notably `projects.rivalAutoStart` lets non-player colonies pursue projects on their
 * own (see `ColonyProjectSystem`); omit it ⇒ only explicit `start()` calls fire.
 */
export type EcosystemWorldConfigExt = EcosystemWorldConfig & {
  readonly projects?: Partial<ProjectsConfig> | undefined;
  /**
   * OPT-IN predator-danger avoidance for colony foragers. ABSENT ⇒ foragers use
   * greedy pathing and the world (and its hash) is byte-identical to a pre-danger
   * build. When set, a `DangerField` is rebuilt each tick from live predator
   * positions (registered BEFORE the colony movement so foragers see this tick's
   * danger) and a bounded, weighted A* — `maxExpansions` cap, the field attached —
   * routes the FORAGE castes (Worker/Specialist) AROUND predator territory. Kept
   * out of the shared types.ts (like `projects`/`timescale`) to avoid a dependency
   * cycle. Tuning:
   *   • maxExpansions — hard cap on A* cells per forager per tick (bounds the hot
   *     path; pick it large enough to detour a nearby spider, small enough to keep
   *     the per-tick cost modest — see the perf note in the handoff).
   *   • predatorCost  — peak extra step cost at a predator's tile (falls linearly to
   *     0 at the danger radius). Higher ⇒ wider detours.
   *   • radius        — Chebyshev radius of each predator's danger zone.
   */
  readonly dangerAvoidance?: DangerAvoidanceConfig | undefined;
};

/** Tuning for the opt-in forager danger-avoidance layer (all integer). */
export interface DangerAvoidanceConfig extends DangerFieldConfig {
  /** Hard cap on A* expansions per forager per tick (bounds the per-tick cost). */
  readonly maxExpansions: number;
}

/**
 * Danger-avoidance defaults (shared by the CLI and any caller). The cap is sized
 * so a forager can fully circumnavigate a default Spider/Frog territory zone (the
 * stamped box is ~(2r+1)^2 ≈ 121 tiles at radius 5) plus headroom for walls; the
 * cost/radius make a predator's lair a real obstacle to skirt without walling the
 * map. Tune against the perf note if the per-tick delta grows.
 */
export function defaultDangerAvoidanceConfig(): DangerAvoidanceConfig {
  return { maxExpansions: 256, predatorCost: 8, radius: 5 };
}

export function buildEcosystemWorld(config: EcosystemWorldConfigExt): EcosystemWorld {
  // The colony RNG stream. Drawing walls/food/colonies from it in the SAME order
  // as buildColonyWorld keeps the colony layer byte-identical regardless of
  // wildlife. Wildlife draws from a SEPARATE stream (below).
  const rng = new Rng(config.seed);
  // The PER-TICK colony-AI decision stream, isolated from worldgen (DESIGN.md §6).
  // Same salt as buildColonyWorld (AI_STREAM_SALT 0xc2b2ae35) so the colony layer
  // behaves consistently across both builders, and distinct from the wildlife/event/
  // apex streams below so worldgen entity-count changes never perturb AI randomness.
  const aiRng = new Rng((config.seed ^ 0xc2b2ae35) >>> 0);

  // Layered-timescale ladder (OPT-IN). When set, it spreads the major system
  // cadences across the four layers (capacity→Growth, AI→Growth, leaderboard→
  // Evolution, projects→Civilization). When undefined, every cadence below keeps its
  // EXACT pre-timescale default, so the world is byte-identical (the hash fold is
  // guarded too). The ladder comes from config (a baked reference tps), NEVER live
  // playback tps — sim state at a tick must be playback-independent for save replay.
  const timescale = config.timescale as TimescaleConfig | undefined;

  // Dynamic ecosystem events are OPT-IN. When config.events is set we create ONE
  // shared modifier struct (the event system WRITES it; resources/ecosystem/
  // organisms READ it). When unset, `mods` stays undefined and every seam takes
  // its neutral path, so the world is byte-identical to a pre-events build.
  const mods: EcosystemModifiers | undefined =
    config.events !== undefined ? neutralModifiers() : undefined;

  // Capacity: the colony bound (founders may exceed steady-state) + food + the
  // wildlife cap + headroom (with events on, a predator burst can add transient
  // wildlife above the steady cap, so reserve the burst count too).
  const peakPerColony = Math.max(config.colony.maxPopulation, config.startMembers);
  // Reserve one event's worth of burst (derived, so a retuned predatorBurst can't
  // outgrow the reserve) + a small slack. Default = 6 + 2 = 8 (unchanged).
  const burstHeadroom = config.events !== undefined ? MAX_PREDATOR_BURST + 2 : 0;
  // Apex headroom: the live boss + a margin for RatKing rat summons.
  const apexHeadroom = config.apex !== undefined ? 16 : 0;
  // Landmark headroom: each landmark seeds a few food nodes + wildlife (and tops up
  // toward a small target), so reserve a generous slot budget per landmark. Uses the
  // AREA-SCALED target (not the bare config.count) so a density-scaled big map reserves
  // enough — must match the count LandmarkSystem will actually place.
  const landmarkHeadroom = config.landmarks !== undefined
    ? landmarkTargetCount(config.landmarks, config.width, config.height) * 8
    : 0;
  // Queen headroom: one immobile KIND.Queen entity per colony when opted in, so the
  // extra root unit never exhausts entity capacity. 0 when off (byte-identical).
  const queenHeadroom = config.colony.queenEntity ? config.colonies : 0;
  const capacity =
    config.colonies * peakPerColony + config.food + 16 + config.ecosystem.maxWildlife + 16 +
    burstHeadroom + apexHeadroom + landmarkHeadroom + queenHeadroom;

  const entities = new EntitySystem(capacity);
  const grid = new WorldGrid(entities, config.width, config.height, config.maxPerTile);
  const resources = new ResourceSystem(entities, grid, config.resource, config.food, mods);

  const baseProfiles = config.castes.map((cfg, caste) => baseProfile(cfg, caste === CASTE.Flyer));
  const genomes = new GenomeSystem(baseProfiles);

  const colonies = new ColonySystem(entities, grid, genomes, config.colony, Math.max(1, config.colonies));
  const pathing = new GreedyPathing(grid);
  const geneConfig = config.gene ?? defaultGeneConfig();
  const genes = new GeneInventory(Math.max(1, config.colonies), geneConfig);

  // --- Autonomy: the task scheduler + the decision engine. ---
  // decisionInterval rides the Growth layer when a ladder is set; otherwise it keeps
  // the unchanged default object (byte-identical).
  const baseAiConfig = config.ai ?? defaultColonyAIConfig();
  const aiConfig = timescale
    ? { ...baseAiConfig, decisionInterval: layerPeriod(timescale, LAYER.Growth) }
    : baseAiConfig;
  const tasks = new TaskQueue(Math.max(1, config.colonies), capacity, aiConfig.tasksPerColony);
  // Emergent colony personality: accumulates each colony's decisions + structural
  // signals (genes, claimed territory) into a named identity that biases the AI.
  // Mirrors buildColonyWorld so the playable ecosystem world grows identities too.
  const personality = new ColonyPersonality(
    Math.max(1, config.colonies), config.personality ?? defaultPersonalityConfig(),
  );
  // ColonyAISystem draws per-tick decision randomness, so it takes the ISOLATED
  // aiRng — NOT the worldgen `rng` — exactly as buildColonyWorld does (DESIGN.md §6).
  const ai = new ColonyAISystem(
    colonies, entities, grid, aiRng, tasks, genes, aiConfig, config.colony.maxPopulation, personality,
  );

  const ctx: AutonomyCtx = {
    entities, grid, rng, colonies, resources, genomes, pathing,
    clock: { tick: 0 }, genes, geneConfig, eventMods: mods,
    // Per-creature movement speed (opt-in; mirrors buildColonyWorld). When
    // config.colony.creatureSpeed is set, `advance` (castes.ts) spends each genome's
    // fixed-point `speed` through the sub-tick accumulator; unset ⇒ the original
    // integer stepsPerTick movement, byte-identical (the determinism gate).
    creatureSpeed: config.colony.creatureSpeed === true,
    tasks, onExpand: ai.onExpand, onScout: ai.onScout,
  };
  const organisms = new OrganismSystem(ctx);

  // --- Read-only colony standings (matches buildColonyWorld). Observes only;
  //     runs last, so it can't perturb the colony or wildlife layers. ---
  // Leaderboard rides the Evolution layer when a ladder is set; otherwise its
  // default cadence is unchanged (byte-identical).
  const baseLeaderboardConfig = config.leaderboard ?? defaultLeaderboardConfig();
  const leaderboard = new LeaderboardSystem(
    colonies, ai, genes,
    timescale
      ? { ...baseLeaderboardConfig, cadence: layerPeriod(timescale, LAYER.Evolution) }
      : baseLeaderboardConfig,
  );

  // --- Sparse walls (~6%), deterministic; drawn first so placement avoids them.
  // Identical to buildColonyWorld (same RNG draws, same order).
  const wallTarget = Math.floor(grid.size * 0.06);
  let wallsPlaced = 0;
  let guard = 0;
  const guardMax = grid.size * 4;
  while (wallsPlaced < wallTarget && guard++ < guardMax) {
    const x = rng.int(config.width);
    const y = rng.int(config.height);
    if (grid.isPassable(x, y)) {
      grid.setPassable(x, y, false);
      wallsPlaced++;
    }
  }

  // --- Food nodes on passable tiles (colony RNG). ---
  placeOnPassable(rng, grid, config.food, (x, y) => {
    resources.addFood(x, y, config.foodAmount);
  });

  // --- Colony nests + founding members on passable tiles (colony RNG). ---
  placeOnPassable(rng, grid, config.colonies, (x, y) => {
    const cid = colonies.createColony(x, y);
    colonies.seedInitialMembers(cid, config.startMembers);
  });

  // Queen preservation: colony 0 is the PLAYER — never accidental extinction (it
  // recovers instead; see colony.ts). Rivals stay mortal.
  if (config.colonies > 0) colonies.protectColony(0);

  // --- Ecosystem wiring: an INDEPENDENT RNG stream so wildlife draws never
  //     perturb colony determinism. ---
  const wildlifeRng = new Rng((config.seed ^ 0x9e3779b9) >>> 0);

  // Colony seams (all optional from wildlife's view — present here because we
  // built colonies, but wildlife works the same with `colonies: 0`).
  const presence = buildPresence(colonies, ai);
  const preyKill: PreyKillSink = {
    onOrganismKilled(id: EntityId): void {
      // Wildlife performs the grid.remove/despawn; we only fix the census.
      colonies.onDeath(entities.colony[id] as number, entities.caste[id] as Caste);
    },
  };
  const food: RaidableFood = {
    raid(id: EntityId, amount: number): number {
      return resources.consume(id, amount);
    },
  };

  const wildlifeCtx: WildlifeCtx = {
    entities, grid, rng: wildlifeRng, pathing, presence, preyKill, food,
  };
  const wildlife = new WildlifeSystem(wildlifeCtx, config.species);
  const ecosystem = new EcosystemSystem(
    wildlife, grid, wildlifeRng, config.ecosystem, presence, mods,
  );

  // Seed initial wildlife AFTER colonies/food/walls so spawns land on open ground.
  ecosystem.seedInitial();

  // --- Forager DANGER AVOIDANCE (opt-in): a predator-cost field + a bounded,
  //     weighted A* that routes the FORAGE castes around predator territory. The
  //     field is rebuilt each tick (registered BELOW, BEFORE the organism loop, so
  //     foragers see THIS tick's danger) and attached to the A*; we LATE-BIND it
  //     onto ctx.foragePathing (the organism loop reads ctx by reference per tick,
  //     and the loop only runs after this builder returns, so the assignment is
  //     deterministic). NO RNG is drawn here, and the field is a derived cache of
  //     already-hashed wildlife state ⇒ enabling this never perturbs ANY stream and
  //     is excluded from computeEcosystemStateHash. ABSENT ⇒ greedy ⇒ byte-identical. ---
  let dangerField: DangerField | undefined;
  if (config.dangerAvoidance !== undefined) {
    const da = config.dangerAvoidance;
    dangerField = new DangerField(entities, grid, wildlife, {
      predatorCost: da.predatorCost,
      radius: da.radius,
    });
    // The HYBRID forage pathing: cheap greedy when the next step is clear, the
    // bounded weighted A* only when danger is on/adjacent to the step (so the A*
    // cost is paid only near predators, not on every forage step — see the perf
    // note). Late-bind onto the shared ctx: the field is readonly at the type level
    // (the organism loop must never reassign it); we set it ONCE here through a
    // narrowly-typed mutable view, before any tick runs. DangerAwarePathing
    // implements Pathing, so the assignment is well-typed.
    const forageHybrid = new DangerAwarePathing(grid, dangerField, da.maxExpansions);
    (ctx as { -readonly [K in 'foragePathing']: AutonomyCtx['foragePathing'] }).foragePathing =
      forageHybrid;
  }

  // --- Dynamic events (opt-in): the scheduler that mutates `mods`. Built AFTER
  //     wildlife + resources exist, on its OWN isolated RNG (independent of the
  //     colony + wildlife streams), so enabling events never perturbs either. ---
  let events: EcosystemEventSystem | undefined;
  if (config.events !== undefined && mods !== undefined) {
    const eventRng = new Rng((config.seed ^ 0x5af3107a) >>> 0);
    events = new EcosystemEventSystem(
      entities, grid, wildlife, resources, mods, eventRng, config.events,
    );
  }

  // --- Apex creatures (opt-in): rare bosses on the wildlife/ecosystem layer. On
  //     its OWN isolated RNG (independent of colony/wildlife/event streams), so
  //     enabling apex never perturbs any of them. ---
  let apex: ApexSystem | undefined;
  if (config.apex !== undefined) {
    const apexRng = new Rng((config.seed ^ 0x7a5c0fe1) >>> 0);
    // COMPOSITE "ecosystem actor" significance + ladder cadence + summon protection
    // folded onto the caller's apex config. The CALLER'S EXPLICIT VALUE ALWAYS WINS:
    // every seam is guarded so a config that sets it is passed through untouched.
    // Closures are built ONCE here (construction), never per-tick, and are integer +
    // deterministic. `leaderboard` is constructed above, so the late-bound closure
    // below resolves a valid standing by the time the loop first runs.
    // The public config type is the base ApexWorldConfig; the behaviour seams are
    // apex.ts-local, so read any caller-supplied override through a widened view.
    const callerApex = config.apex as ApexWorldConfig & Partial<ApexBehaviorConfig>;
    // SIGNIFICANCE: respect WHATEVER the caller configured. `significanceOf` is a
    // WHOLE-SCORE hook that SUPPRESSES the granular `territoryWeightOf`/`hiveWeightOf`
    // path (see apex.ts significanceScore), so we only inject our composite when the
    // caller supplied NO significance signal at all — injecting a whole-score hook over
    // a caller's granular weights would silently nullify them.
    const callerHasSignificance =
      callerApex.significanceOf !== undefined ||
      callerApex.territoryWeightOf !== undefined ||
      callerApex.hiveWeightOf !== undefined;
    const sigSeam: ApexBehaviorConfig = callerHasSignificance
      ? {}
      : {
          // The composite "ecosystem actor" score that REPLACES population-only: fold
          // population + hive-node count + leaderboard standing. It is always >= the
          // population baseline (both bonuses are non-negative), so a colony is never
          // made LESS significant than under population-only. rankAt is 0 for the
          // leader, so (count - rank) gives the top colony the largest standing bonus;
          // before the board's first recompute every rank is 0 (a uniform bonus).
          significanceOf: (cid: number): number => {
            const pop = colonies.populationAt(cid);
            const hive = ai.hiveCountAt(cid);
            const standing = colonies.count - leaderboard.rankAt(cid);
            return pop + hive + (standing > 0 ? standing : 0);
          },
          // hiveWeightOf is the granular fallback (subsumed by significanceOf above, but
          // kept so a future caller dropping only significanceOf still gets hive weight).
          hiveWeightOf: (cid: number): number => ai.hiveCountAt(cid),
        };
    const apexBehavior: ApexBehaviorConfig = {
      ...sigSeam,
      // Migration rides the Evolution layer when a ladder is set (else the literal
      // migrateInterval); the ApexSystem resolves the interval once at construction.
      // Only INCLUDE the ladder when one actually exists (caller's, else the world's),
      // so a timescale-off world omits the field and keeps the literal migrateInterval
      // (byte-identical) — exactOptionalPropertyTypes forbids an explicit `undefined`.
      ...(callerApex.timescale ?? timescale
        ? { timescale: (callerApex.timescale ?? timescale) as TimescaleConfig }
        : {}),
      // Stop the Rat-King eating its own fresh summons for a sensible window (6x the
      // default RatKing summon cadence of 8) so reinforcements can disperse first.
      protectSummonsTicks: callerApex.protectSummonsTicks ?? 48,
    };
    const apexConfig: ApexWorldConfig & ApexBehaviorConfig = { ...config.apex, ...apexBehavior };
    apex = new ApexSystem(entities, grid, pathing, colonies, genes, wildlife, apexRng, apexConfig);
  }

  // --- Ecosystem PRESSURE (opt-in): senses wildlife around each nest and feeds the
  //     colony AI so it adapts (more soldiers, suppressed flyers, robbed larder). No
  //     RNG — pure integer scan. Late-bound into the AI (breaks the build cycle). ---
  let pressure: EcosystemPressureSystem | undefined;
  if (config.pressure !== undefined) {
    pressure = new EcosystemPressureSystem(colonies, wildlife, entities, grid, config.pressure);
    ai.attachPressure(pressure);
    // Late-bind the hive-node anchors so the pressure scan covers the nest AND every
    // founded hive node (ColonyAISystem satisfies HiveAnchorsRead directly). A threat
    // camped on a remote node is now FELT; a nest-only world is byte-identical (this
    // only adds anchors a colony actually founded). Closure-free, no per-tick alloc.
    pressure.attachAnchors(ai);
    // QUEEN THREAT (opt-in): when the queen entity is enabled, drive her damage from the
    // same proximity-weighted predation+aerial pressure the colony already senses. The
    // closure is built ONCE here (never per-tick), is integer + deterministic, and only
    // damages — regen lives in colonies.update. NOTE on ORDERING: the system order is
    // events→…→organisms→colonies→…→pressure, so colonies.update reads LAST tick's
    // pressure (a deterministic 1-tick lag; pressure reads 0 on tick 0). Off ⇒ unwired.
    if (config.colony.queenEntity) {
      const p = pressure;
      colonies.attachQueenThreat((cid: ColonyId): number => {
        const t = p.predationAt(cid) + p.aerialAt(cid);
        if (t <= QUEEN_THREAT_FLOOR) return 0;
        const d = (t - QUEEN_THREAT_FLOOR) >> QUEEN_THREAT_SHIFT;
        return d > QUEEN_THREAT_CAP ? QUEEN_THREAT_CAP : d;
      });
    }
  }

  // --- Named LANDMARKS (opt-in): major map features that AUGMENT the already
  //     laid-out world — renewable food larders, persistent wildlife territories,
  //     and impassable barriers/chokepoints — so the map reads as legible PLACES
  //     rather than procedural noise. On its OWN isolated RNG (independent of the
  //     colony/wildlife/event/apex streams), generated AFTER the base world so it
  //     only ADDS to it. Absent ⇒ the world is byte-identical to a pre-landmark
  //     build (the hash fold is guarded). ---
  let landmarks: LandmarkSystem | undefined;
  if (config.landmarks !== undefined) {
    const landmarkRng = new Rng((config.seed ^ 0x1a4d3c7b) >>> 0);
    landmarks = new LandmarkSystem(
      entities, grid, resources, colonies, wildlife, landmarkRng, config.landmarks,
    );
    landmarks.generate();
    // Fold the landmark expansion-desirability into the colony AI's growth drive (food
    // hubs draw expansion, barriers/predator-nests hold it back). Late-bound so the
    // one-way landmark→AI read needs no construction reorder; only when landmarks exist,
    // so a no-landmark world is byte-identical (the AI never reads the seam).
    ai.attachLandmarks(landmarks);
  }

  // --- Queen ENTITY (opt-in): a REAL immobile KIND.Queen at the PROTECTED player
  //     colony's nest (protectColony(0) ran above). Spawned LAST among initial
  //     entities so she takes the highest setup id and shifts no other entity's id;
  //     `spawnQueen` draws NO RNG and never touches the grid, so enabling her cannot
  //     perturb any colony/wildlife/event/apex stream — the world stays deterministic.
  //     Off ⇒ no entity, byte-identical. Her HP folds into the hash via the generic
  //     active-entity walk (no separate fold). ---
  if (config.colony.queenEntity && config.colonies > 0) colonies.spawnQueen(0);

  // --- Fixed system order: [events] → ai → scheduler → organisms → colonies →
  //     resources → ecosystem (population) → wildlife (behaviour) → leaderboard.
  //     Events run FIRST so the modifiers it sets apply to every system this tick.
  const scheduler: System = {
    name: 'task-scheduler',
    update(_tick: Tick): void {
      tasks.assignOpenTasks(ctx);
    },
  };

  // Region-scale colony projects: the long-term build layer. Reads colony census +
  // food only, so it slots in without touching the colony layer; runs after the
  // colony update so it sees the settled census and pays dividends into next tick.
  // Projects ride the Civilization layer (the slowest) when a ladder is set;
  // otherwise the default cadence is unchanged (byte-identical).
  // Base config (+ Civilization cadence when a ladder is set), then any caller
  // override merged on top. Absent `config.projects` ⇒ base only ⇒ byte-identical.
  const projectsBase = timescale
    ? { ...defaultProjectsConfig(), cadence: layerPeriod(timescale, LAYER.Civilization) }
    : defaultProjectsConfig();
  const projects = new ColonyProjectSystem(
    colonies,
    config.projects ? { ...projectsBase, ...config.projects } : projectsBase,
  );

  // PHYSICAL HIVE (OPT-IN): realises the AI's founded nodes into on-map chambers and
  // runs their effects (storage food yield, military turret). Built BEFORE capacity so
  // the chamber count can feed the ceiling. ABSENT ⇒ no system, no rng draws in the loop,
  // byte-identical to a hive-less build. The constructor is rng-neutral (it only allocates
  // SoA), so even constructing it perturbs nothing — only its `update` draws (room gen).
  const hive = config.hive !== undefined
    ? new HiveSystem(colonies, ai, entities, grid, rng, genes, config.hive)
    : undefined;
  // Late-bind the hive into the AI so it can post tending tasks (Nurse/Haul/Repair) and
  // read chamber state — mirrors buildColonyWorld; a no-op reader, draws no rng.
  if (hive !== undefined) ai.attachHive(hive);

  // CARRYING CAPACITY — the population-stability foundation. Capacity derives from
  // food income + storage + hive nodes + completed projects; it governs the colony
  // spawn loop (faster below cap, gated at cap) + the AI's Reproduction need, and
  // culls/wastes over capacity. Late-bound into the colony + AI to break the cycle.
  // Capacity recompute rides the Growth layer when a ladder is set; otherwise the
  // default cadence is unchanged (byte-identical).
  const baseCapacityConfig = defaultCapacityConfig(config.colony.maxPopulation);
  const capacitySystem = new CarryingCapacitySystem(
    colonies, entities, grid,
    timescale
      ? { ...baseCapacityConfig, cadence: layerPeriod(timescale, LAYER.Growth) }
      : baseCapacityConfig,
    {
      hiveNodesOf: (cid) => ai.hiveCountAt(cid),
      // Physical chambers lift the ceiling too, but ONLY when a hive is wired — when
      // absent the key stays off the inputs object, so capacity is byte-identical.
      ...(hive !== undefined ? { hiveChambersOf: (cid: ColonyId) => hive.chamberCountOf(cid) } : {}),
      projectsOf: (cid) => projects.completedCountOf(cid),
    },
  );
  colonies.attachReproduction(capacitySystem);
  ai.attachCapacity(capacitySystem);

  // --- Evolution DISCOVERY (opt-in): watches each colony's gene pool and auto-discovers
  //     emergent "morph" species. PURE + observe-only (no RNG, mutates only its own
  //     columns), so adding it never perturbs world behaviour; and it's a DERIVED view of
  //     already-hashed gene state, so it's left OUT of the hash (recomputes on replay). It
  //     shares the world clock (ctx.clock) the loop advances. Absent ⇒ byte-identical. ---
  let discovery: DiscoverySystem | undefined;
  if (config.discovery !== undefined) {
    discovery = new DiscoverySystem(
      genes, { colonyCount: () => colonies.count }, config.discovery, colonies.capacity, ctx.clock,
    );
  }

  const loop = new GameLoop();
  if (events !== undefined) loop.add(events);
  // Capacity recomputes FIRST so the colony spawn loop sees a fresh ceiling this tick.
  loop.add(capacitySystem);
  loop.add(ai).add(scheduler);
  // Danger field rebuilds IMMEDIATELY BEFORE the organism loop so foragers route on
  // the current view of predator positions (as of the end of the previous tick —
  // wildlife moves later this tick, a deterministic 1-step lag matching pressure).
  // Only present when dangerAvoidance is opted in (else greedy ⇒ byte-identical).
  if (dangerField !== undefined) loop.add(dangerField);
  loop
    .add(organisms)
    .add(colonies)
    .add(resources)
    .add(ecosystem)
    .add(wildlife);
  if (apex !== undefined) loop.add(apex);
  // Pressure runs AFTER wildlife moves (fresh positions); the AI reads it next tick.
  if (pressure !== undefined) loop.add(pressure);
  // Landmarks run late (after wildlife) — they only periodically top up a territory's
  // population; the spawned wildlife acts from next tick. Order is fixed + isolated.
  if (landmarks !== undefined) loop.add(landmarks);
  // The hive runs LATE (after movement) so its military turret targets fresh positions
  // and it realises any node the AI founded THIS tick. Absent ⇒ never added ⇒ byte-identical.
  if (hive !== undefined) loop.add(hive);
  // Discovery runs late (after gene drops settle this tick) and just before the leaderboard.
  // Observe-only + drawing no RNG, its position can't change any hashed state — fixed for
  // determinism. Absent ⇒ never added ⇒ byte-identical.
  if (discovery !== undefined) loop.add(discovery);
  loop.add(leaderboard).add(projects);

  return {
    config: config as unknown as ColonyWorld['config'],
    rng, entities, grid, loop, resources, colonies, organisms,
    genes, genomes, tasks, ai, personality, leaderboard,
    wildlife, ecosystem, wildlifeRng,
    events, eventMods: mods,
    apex,
    pressure,
    projects,
    capacity: capacitySystem,
    landmarks,
    // Present only when wired (ColonyWorld.hive is exact-optional ⇒ absent, not undefined).
    ...(hive !== undefined ? { hive } : {}),
    timescale,
    discovery,
    // Per-creature movement speed flag (mirrors buildColonyWorld). Drives the GUARDED
    // moveAccum fold in computeColonyStateHash (which computeEcosystemStateHash calls).
    // Default false ⇒ byte-identical to a pre-feature ecosystem world.
    creatureSpeed: config.colony.creatureSpeed === true,
    dangerField,
  };
}

/** Find a passable tile (deterministic, RNG-driven) and invoke `place(x,y)`. */
function placeOnPassable(
  rng: Rng,
  grid: WorldGrid,
  count: number,
  place: (x: number, y: number) => void,
): void {
  for (let i = 0; i < count; i++) {
    let x = 0;
    let y = 0;
    let tries = 0;
    do {
      x = rng.int(grid.width);
      y = rng.int(grid.height);
      tries++;
    } while (!grid.isPassable(x, y) && tries < 64);
    if (grid.isPassable(x, y)) place(x, y);
  }
}

/** Advance the world by `ticks` ticks. */
export function runHeadless(world: EcosystemWorld, ticks: number): void {
  world.loop.step(ticks);
}

// ---------------------------------------------------------------------------
// Deterministic state fingerprint (colony + organism + wildlife state).
// ---------------------------------------------------------------------------

export function computeEcosystemStateHash(world: EcosystemWorld): number {
  // The colony hash already folds the FULL active-entity set (incl. wildlife
  // generic columns: kind/pos/energy/hp/state/…), plus colony/ai/tasks/genes.
  let h = computeColonyStateHash(world);

  const e = world.entities;
  const wildlife = world.wildlife;
  const ecosystem = world.ecosystem;

  // Wildlife-only per-unit columns, in active-set order (same order the colony
  // hash walked the entity set, so this is deterministic).
  const active = e.activeView();
  const n = e.aliveCount;
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((e.kind[id] as number) !== KIND.Wildlife) continue;
    h = fnvU32(h, wildlife.species[id] as number);
    h = fnvU32(h, wildlife.homeX[id] as number);
    h = fnvU32(h, wildlife.homeY[id] as number);
  }

  // Wildlife + ecosystem counters.
  h = fnvU32(h, wildlife.spawnedTotal);
  h = fnvU32(h, wildlife.predationKills);
  h = fnvU32(h, wildlife.foodRaided);
  h = fnvU32(h, wildlife.starvedTotal);
  for (let sp = 0; sp < SPECIES_COUNT; sp++) h = fnvU32(h, wildlife.populationOf(sp as Species));
  h = fnvU32(h, ecosystem.maintenancePasses);
  h = fnvU32(h, ecosystem.spawnedByPassTotal);

  // Dynamic-event state — GUARDED so an events-off world is byte-identical to a
  // pre-events hash (this whole block is skipped when no event system is wired).
  const events = world.events;
  if (events) {
    h = fnvU32(h, events.activeEvent());
    h = fnvU32(h, events.remainingTicks());
    h = fnvU32(h, events.ticksToNextEvent());
    h = fnvU32(h, events.foodRegenPct());
    h = fnvU32(h, events.wildlifeCapacityPct());
    h = fnvU32(h, events.geneDropPct());
    h = fnvU32(h, events.organismDrain());
    h = fnvU32(h, events.eventsFiredTotal);
    for (let ev = 1; ev <= ECO_EVENT_COUNT; ev++) h = fnvU32(h, events.firedOf(ev as EcoEvent));
  }

  // Apex (boss) state — GUARDED so an apex-off world is byte-identical to a
  // pre-apex hash (skipped entirely when no apex system is wired).
  if (world.apex) h = fnvU32(h, world.apex.hash());
  // Ecosystem-pressure state — GUARDED so a pressure-off world is byte-identical to
  // a pre-pressure hash (the per-colony pressure + theft counters are skipped here,
  // though the colony hash already reflects any food the theft drained).
  const pressure = world.pressure;
  if (pressure) {
    const cc = world.colonies.count;
    for (let cid = 0; cid < cc; cid++) {
      h = fnvU32(h, pressure.predationAt(cid));
      h = fnvU32(h, pressure.aerialAt(cid));
      h = fnvU32(h, pressure.theftAt(cid));
    }
    h = fnvU32(h, pressure.scansTotal);
    h = fnvU32(h, pressure.foodStolenTotal);
  }
  // Region-scale projects — GUARDED so a project-less world is byte-identical.
  if (world.projects) h = fnvU32(h, world.projects.hash());
  // Carrying capacity — GUARDED so a capacity-less world is byte-identical.
  if (world.capacity) h = fnvU32(h, world.capacity.hash());
  // Layered timescale — GUARDED so a timescale-off world is byte-identical to a
  // pre-timescale hash (skipped entirely when no ladder is configured).
  if (world.timescale) h = hashTimescale(h, world.timescale);
  // Named landmarks — GUARDED so a landmark-less world is byte-identical to a
  // pre-landmark hash (skipped entirely when no landmark system is wired).
  if (world.landmarks) h = world.landmarks.hash(h, fnvU32);
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// Debug rendering — the colony map with wildlife glyphs overlaid.
// ---------------------------------------------------------------------------

/** Glyph per SPECIES.* (Spider, Bird, Rat, Frog, Beetle, Dragonfly). */
const SPECIES_GLYPH: readonly string[] = ['S', 'B', 'R', 'F', 'E', 'D'];

/** Glyph per APEX_KIND.* (Huntsman, CrowMatriarch, RatKing, PondLeviathan). */
const APEX_GLYPH: readonly string[] = ['H', 'C', 'K', 'L'];

/** Human name per APEX_KIND.* (CLI / census). */
const APEX_NAME: readonly string[] = ['Huntsman', 'CrowMatriarch', 'RatKing', 'PondLeviathan'];

/** Name of an apex kind (defensive bounds-clamp; '-' when no boss). */
export function apexKindName(kind: number): string {
  if (kind < 0 || kind >= APEX_KIND_COUNT) return '-';
  return APEX_NAME[kind] as string;
}

/**
 * ASCII map: the colony render with wildlife overlaid on top (wildlife sit ABOVE
 * organisms so predator pressure is visible). S=Spider B=Bird R=Rat F=Frog.
 */
export function renderEcosystem(world: EcosystemWorld): string {
  const base = renderColony(world);
  const { grid, entities } = world;
  const W = grid.width;
  const H = grid.height;
  // Split the colony render back into a mutable cell grid (drop trailing '\n').
  const rows = base.split('\n');
  const cells = new Array<string>(W * H);
  for (let y = 0; y < H; y++) {
    const row = rows[y] ?? '';
    for (let x = 0; x < W; x++) cells[y * W + x] = row[x] ?? '.';
  }
  // Landmarks mark their centre UNDER live creatures: an empty landmark reads as
  // its glyph (Y/@/V/*/#/~); a creature standing on it overlays on top (so a Bird
  // on its Nest still shows 'B'). Makes important places immediately recognisable.
  const lm = world.landmarks;
  if (lm) {
    const k = lm.count();
    for (let i = 0; i < k; i++) {
      cells[(lm.yAt(i)) * W + lm.xAt(i)] = landmarkGlyph(lm.typeAt(i));
    }
  }
  const active = entities.activeView();
  const n = entities.aliveCount;
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) !== KIND.Wildlife) continue;
    const sp = world.wildlife.species[id] as number;
    cells[(entities.posY[id] as number) * W + (entities.posX[id] as number)] =
      SPECIES_GLYPH[sp] as string;
  }
  // A live boss draws ON TOP of everything: a distinct uppercase glyph by kind.
  const apex = world.apex;
  if (apex && apex.bossAlive()) {
    cells[apex.bossY() * W + apex.bossX()] = APEX_GLYPH[apex.bossKind()] ?? 'B';
  }
  let out = '';
  for (let y = 0; y < H; y++) out += cells.slice(y * W, y * W + W).join('') + '\n';
  return out;
}

/** Per-species + pressure census (CLI / tests). Walks in deterministic order. */
export function ecoCensus(world: EcosystemWorld): string {
  const w = world.wildlife;
  const eco = world.ecosystem;
  let s = '';
  for (let sp = 0; sp < SPECIES_COUNT; sp++) {
    s += `  ${SPECIES_GLYPH[sp] as string}=${String(w.populationOf(sp as Species)).padStart(3)}`;
  }
  s +=
    ` | kills=${w.predationKills} raided=${w.foodRaided}` +
    ` cap=${eco.carryingCapacity()} anchors=${anchorCountOf(world)}\n`;
  return s;
}

/** Anchor count via the live presence (nests + hive nodes). */
function anchorCountOf(world: EcosystemWorld): number {
  let total = world.colonies.count;
  for (let cid = 0; cid < world.colonies.count; cid++) total += world.ai.hiveCountAt(cid);
  return total;
}

/** Human name per ECO_EVENT.* (index 0 = None). CLI / census only. */
const ECO_EVENT_NAME: readonly string[] = [
  'None', 'Pesticide', 'HeatWave', 'Flooding', 'PredatorMigration', 'FoodBloom',
];

/** Name of an event id (defensive bounds-clamp). */
export function ecoEventName(ev: EcoEvent): string {
  const i = ev as number;
  return (ECO_EVENT_NAME[i] ?? 'None') as string;
}

/**
 * Dynamic-event census line (CLI `--events`): the active event, its remaining
 * ticks, the live modifiers, and the per-type fired counts. Empty when events
 * are off (so the events-off CLI prints exactly the pre-events output).
 */
export function eventCensus(world: EcosystemWorld): string {
  const ev = world.events;
  if (!ev) return '';
  let s =
    `  event=${ecoEventName(ev.activeEvent()).padEnd(17)} rem=${String(ev.remainingTicks()).padStart(3)}` +
    ` next=${String(ev.ticksToNextEvent()).padStart(3)} fired=${ev.eventsFiredTotal}` +
    ` | mods food=${ev.foodRegenPct()}% cap=${ev.wildlifeCapacityPct()}%` +
    ` drop=${ev.geneDropPct()}% drain=${ev.organismDrain()} | by[`;
  for (let i = 1; i <= ECO_EVENT_COUNT; i++) {
    s += `${ECO_EVENT_NAME[i] as string}=${ev.firedOf(i as EcoEvent)}${i < ECO_EVENT_COUNT ? ' ' : ''}`;
  }
  s += ']\n';
  return s;
}

/**
 * Apex (boss) census line(s) (CLI `--apex`): the live boss (kind/hp/pos/ticks-
 * alive/top-threat colony), recent sighting ALERTS, and the DEFEAT HISTORY (the
 * milestones: who beat which boss, or that it escaped). Empty when apex is off.
 */
export function apexCensus(world: EcosystemWorld): string {
  const a = world.apex;
  if (!a) return '';
  const tick = world.loop.tick;
  let s = '  apex: ';
  if (a.bossAlive()) {
    const threat = a.topThreatColony();
    s +=
      `${apexKindName(a.bossKind())} hp=${a.bossHpVal()}/${a.bossMaxHp()}` +
      ` @(${a.bossX()},${a.bossY()}) alive=${a.bossTicksAlive(tick)}t` +
      ` threat=${threat === NIL ? '-' : `c${threat}(${a.damageBy(threat)})`}`;
  } else {
    s += 'none';
  }
  s += ` | spawned=${a.spawnedTotal} defeated=${a.defeatedTotal} escaped=${a.escapedTotal}` +
    ` summoned=${a.summonedTotal} devoured=${a.devouredTotal} preyKills=${a.preyKills}\n`;

  // Recent alerts (most recent few, newest last).
  const an = a.alertCount();
  if (an > 0) {
    s += '    alerts:';
    const from = an > 4 ? an - 4 : 0;
    for (let i = from; i < an; i++) {
      s += ` [${apexKindName(a.alertKindAt(i))}@t${a.alertTickAt(i)}(${a.alertXAt(i)},${a.alertYAt(i)})]`;
    }
    s += '\n';
  }
  // Defeat history (the milestones, newest last).
  const dn = a.defeatCount();
  if (dn > 0) {
    s += '    history:';
    const from = dn > 4 ? dn - 4 : 0;
    for (let i = from; i < dn; i++) {
      const outcome = a.defeatOutcomeAt(i) === APEX_OUTCOME.Defeated ? 'slain' : 'escaped';
      const victor = a.defeatVictorAt(i);
      s += ` [${apexKindName(a.defeatKindAt(i))} ${outcome}` +
        `${outcome === 'slain' && victor !== NIL ? ` by c${victor}` : ''}` +
        ` @t${a.defeatTickAt(i)} (${a.defeatTicksAliveAt(i)}t)]`;
    }
    s += '\n';
  }
  return s;
}

/** Short need label for the pressure census (index = NEED.*). */
const NEED_LABEL: readonly string[] = ['Food', 'Def', 'Exp', 'Repro', 'Evo'];

/**
 * Ecosystem-pressure census (CLI `--pressure`): per colony, the sensed Predation /
 * Aerial / Theft pressure, the adaptation FLAGS ([F]orage-pulled-in / [A]erial-
 * flyer-suppressed), and the observable response (Soldiers / Flyers / food / current
 * priority) so the wildlife→behaviour loop is legible. Empty when pressure is off.
 */
export function pressureCensus(world: EcosystemWorld): string {
  const p = world.pressure;
  if (!p) return '';
  const c = world.colonies;
  const ai = world.ai;
  let s = `  pressure: scans=${p.scansTotal} stolen=${p.foodStolenTotal}\n`;
  for (let cid = 0; cid < c.count; cid++) {
    if (!c.isAlive(cid)) { s += `    c${cid}: extinct\n`; continue; }
    const flags = `${p.foragePulledIn(cid) ? 'F' : '-'}${p.flyersSuppressed(cid) ? 'A' : '-'}`;
    s +=
      `    c${cid}: pred=${p.predationAt(cid)} aerial=${p.aerialAt(cid)} theft=${p.theftAt(cid)} [${flags}]` +
      ` → S=${c.casteCountAt(cid, CASTE.Soldier)} Fl=${c.casteCountAt(cid, CASTE.Flyer)}` +
      ` food=${c.foodAt(cid)} pri=${NEED_LABEL[ai.priorityAt(cid)] ?? '?'}\n`;
  }
  return s;
}

/**
 * Bottleneck diagnostics census (CLI `--diag`): per colony, the OBSERVE-ONLY
 * read-model — the single binding growth limiter, capacity utilisation, pop/cap,
 * expected growth, and resource flow (in/store/out). Pure: it reads the live colony +
 * capacity (+ pressure/ai) state and never mutates or hashes. Empty when no capacity
 * system is wired (the read-model needs the capacity reader).
 */
export function diagCensus(world: EcosystemWorld): string {
  const cap = world.capacity;
  if (!cap) return '';
  const colonyCfg = (world.config as unknown as EcosystemWorldConfig).colony;
  const ctx: DiagnosticsCtx = {
    colony: world.colonies,
    capacity: cap,
    pressure: world.pressure,
    ai: world.ai,
    spawnCost: colonyCfg.spawnCost,
    spawnCooldown: colonyCfg.spawnCooldown,
  };
  let s = '  diagnostics:\n';
  for (let cid = 0; cid < world.colonies.count; cid++) {
    const d = computeColonyDiagnostics(ctx, cid);
    s += `    ${diagnosticsCensusLine(d)} (${d.trendName})\n`;
  }
  return s;
}

/**
 * Named-landmark census (CLI `--landmarks`): each landmark's glyph, name, position
 * and its signed EXPANSION BIAS at the centre (＋ fertile, draws growth / − hazard
 * or barrier, repels), plus the wildlife-respawn tally that keeps territories alive.
 * Empty when landmarks are off (so the landmarks-off CLI prints the prior output).
 */
export function landmarkCensus(world: EcosystemWorld): string {
  const lm = world.landmarks;
  if (!lm) return '';
  const k = lm.count();
  let s = `  landmarks: n=${k} seeded=${lm.wildlifeSpawnedTotal} respawns=${lm.respawnPasses}\n`;
  for (let i = 0; i < k; i++) {
    const x = lm.xAt(i);
    const y = lm.yAt(i);
    const bias = lm.expansionBiasAt(x, y);
    s +=
      `    ${landmarkGlyph(lm.typeAt(i))} ${landmarkName(lm.typeAt(i)).padEnd(13)}` +
      ` @(${String(x).padStart(2)},${String(y).padStart(2)}) bias=${bias > 0 ? '+' : ''}${bias}\n`;
  }
  return s;
}

// ---------------------------------------------------------------------------
// CLI wrapper (mirrors colonyExample).
// ---------------------------------------------------------------------------

interface CliFlags {
  seed: number; width: number; height: number; colonies: number; members: number;
  food: number; ticks: number; every: number; quiet: boolean; map: boolean;
  /** Dynamic ecosystem events — ON by default; `--no-events` omits them. */
  events: boolean;
  /** Apex creatures / bosses — OFF by default; `--apex` enables them. */
  apex: boolean;
  /** Wildlife→colony pressure — ON by default; `--no-pressure` omits it. */
  pressure: boolean;
  /** Named map landmarks — ON by default; `--no-landmarks` omits them. */
  landmarks: boolean;
  /** Bottleneck diagnostics census — OFF by default; `--diag` prints it (observe-only). */
  diag: boolean;
  /** Physical hive (chambers + effects) — OFF by default; `--hive` enables it. */
  hive: boolean;
}

function parseFlags(argv: readonly string[]): CliFlags {
  const f: CliFlags = {
    seed: 1, width: 48, height: 24, colonies: 2, members: 8,
    food: 28, ticks: 200, every: 25, quiet: false, map: false,
    events: true, apex: false, pressure: true, landmarks: true, diag: false, hive: false,
  };
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i] as string;
    switch (a) {
      case '--seed': f.seed = intArg(argv, ++i, f.seed); break;
      case '--width': f.width = intArg(argv, ++i, f.width); break;
      case '--height': f.height = intArg(argv, ++i, f.height); break;
      case '--colonies': f.colonies = intArg(argv, ++i, f.colonies); break;
      case '--members': f.members = intArg(argv, ++i, f.members); break;
      case '--food': f.food = intArg(argv, ++i, f.food); break;
      case '--ticks': f.ticks = intArg(argv, ++i, f.ticks); break;
      case '--every': f.every = intArg(argv, ++i, f.every); break;
      case '--quiet': f.quiet = true; break;
      case '--map': f.map = true; break;
      case '--events': f.events = true; break;
      case '--no-events': f.events = false; break;
      case '--apex': f.apex = true; break;
      case '--pressure': f.pressure = true; break;
      case '--no-pressure': f.pressure = false; break;
      case '--landmarks': f.landmarks = true; break;
      case '--no-landmarks': f.landmarks = false; break;
      case '--diag': f.diag = true; break;
      case '--hive': f.hive = true; break;
      default: break;
    }
  }
  return f;
}

function intArg(argv: readonly string[], i: number, fallback: number): number {
  const raw = argv[i];
  if (raw === undefined) return fallback;
  const v = Number.parseInt(raw, 10);
  return Number.isFinite(v) ? v : fallback;
}

function configFromFlags(f: CliFlags): EcosystemWorldConfig {
  return {
    seed: f.seed, width: f.width, height: f.height,
    colonies: f.colonies, startMembers: f.members,
    food: f.food, foodAmount: 16, maxPerTile: 4,
    castes: defaultCasteConfigs(), colony: defaultColonyConfig(),
    resource: { regenDelay: 14, regenAmount: 16 },
    species: defaultSpeciesConfigs(), ecosystem: defaultEcosystemConfig(),
    // Events default ON in the demo so the disturbance layer is visible; omitted
    // (undefined) under --no-events so the world is byte-identical to pre-events.
    ...(f.events ? { events: defaultEcosystemEventConfig() } : {}),
    // Apex is OFF unless --apex; omitted (undefined) so an apex-off world is
    // byte-identical to a pre-apex build.
    ...(f.apex ? { apex: defaultApexConfig() } : {}),
    // Pressure ON in the demo so the wildlife→colony adaptation is visible; omitted
    // (undefined) under --no-pressure so the world is byte-identical to pre-pressure.
    ...(f.pressure ? { pressure: defaultEcosystemPressureConfig() } : {}),
    // Landmarks ON in the demo so the map reads as legible places; omitted
    // (undefined) under --no-landmarks so the world is byte-identical to pre-landmarks.
    ...(f.landmarks ? { landmarks: defaultLandmarkConfig() } : {}),
    // Physical hive OFF unless --hive; omitted (undefined) so a hive-less world is
    // byte-identical to a pre-hive build.
    ...(f.hive ? { hive: defaultHiveConfig() } : {}),
  };
}

function runCli(argv: readonly string[]): void {
  const flags = parseFlags(argv);
  const config = configFromFlags(flags);
  const world = buildEcosystemWorld(config);

  if (flags.quiet) {
    const start = process.hrtime.bigint();
    runHeadless(world, flags.ticks);
    const elapsedMs = Number(process.hrtime.bigint() - start) / 1e6;
    console.log(
      `quiet: colonies=${flags.colonies} ticks=${flags.ticks} grid=${flags.width}x${flags.height} | ` +
        `elapsed=${elapsedMs.toFixed(2)}ms ticks/sec=${(flags.ticks / (elapsedMs / 1000)).toFixed(0)} ` +
        `wildlife=${world.wildlife.population()} kills=${world.wildlife.predationKills} ` +
        `raided=${world.wildlife.foodRaided} organisms=${world.colonies.totalPopulation()} ` +
        `${eventSummaryFragment(world)}${apexSummaryFragment(world)}` +
        `hash=${hashHex(computeEcosystemStateHash(world))}`,
    );
    return;
  }

  const small = world.grid.width <= 80 && world.grid.height <= 48;
  printFrame(world, flags, small);
  let done = 0;
  while (done < flags.ticks) {
    const chunk = Math.min(flags.every, flags.ticks - done);
    runHeadless(world, chunk);
    done += chunk;
    printFrame(world, flags, small);
  }
  console.log(
    `\nsummary: seed=${flags.seed} ticks=${world.loop.tick} ` +
      `wildlifeSpawned=${world.wildlife.spawnedTotal} kills=${world.wildlife.predationKills} ` +
      `raided=${world.wildlife.foodRaided} starved=${world.wildlife.starvedTotal} ` +
      `passes=${world.ecosystem.maintenancePasses} ` +
      `${eventSummaryFragment(world)}${apexSummaryFragment(world)}` +
      `finalHash=${hashHex(computeEcosystemStateHash(world))}`,
  );
}

/**
 * Compact `event=<name> rem=<n> fired=<total> ` fragment for one-line CLI output
 * (trailing space so it slots between fields). Empty when events are off.
 */
function eventSummaryFragment(world: EcosystemWorld): string {
  const ev = world.events;
  if (!ev) return '';
  return `event=${ecoEventName(ev.activeEvent())} rem=${ev.remainingTicks()} fired=${ev.eventsFiredTotal} `;
}

/**
 * Compact `apex=<name|none> spawned/defeated/escaped ` fragment for one-line CLI
 * output (trailing space). Empty when apex is off.
 */
function apexSummaryFragment(world: EcosystemWorld): string {
  const a = world.apex;
  if (!a) return '';
  const live = a.bossAlive() ? apexKindName(a.bossKind()) : 'none';
  return `apex=${live} spawned=${a.spawnedTotal} defeated=${a.defeatedTotal} escaped=${a.escapedTotal} `;
}

function printFrame(world: EcosystemWorld, flags: CliFlags, small: boolean): void {
  console.log(
    `tick=${String(world.loop.tick).padStart(4)} ` +
      `wildlife=${String(world.wildlife.population()).padStart(3)} ` +
      `organisms=${String(world.colonies.totalPopulation()).padStart(4)} ` +
      `kills=${String(world.wildlife.predationKills).padStart(4)} ` +
      `raided=${String(world.wildlife.foodRaided).padStart(5)}`,
  );
  process.stdout.write(ecoCensus(world));
  process.stdout.write(eventCensus(world));
  process.stdout.write(apexCensus(world));
  process.stdout.write(pressureCensus(world));
  process.stdout.write(landmarkCensus(world));
  if (flags.diag) process.stdout.write(diagCensus(world));
  if (flags.map && small) process.stdout.write(renderEcosystem(world));
}

const isMain =
  typeof process !== 'undefined' &&
  Array.isArray(process.argv) &&
  process.argv[1] !== undefined &&
  import.meta.url === `file://${process.argv[1]}`;

if (isMain) runCli(process.argv.slice(2));

// Re-export so tests/CLI can build species cycles without reaching into types.
export { SPECIES, NIL };
